import type { WarehouseResponse } from '@palaccount/contracts';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { Typeahead, type TypeaheadOption } from '@/components/ui/typeahead';
import { useWarehouses } from '@/features/inventory/stock-api';

/**
 * THE REPORT WAREHOUSE FILTER (BR-DSH-10-17) — DSH-05, DSH-06 and DSH-07's
 * Band 2 dropdown, which no report screen had ever rendered.
 *
 * The rule: *"The Warehouse / Branch filter on every report screen must read
 * the warehouse master, and must offer exactly the warehouses that hold stock
 * — the same set the stock screens offer. It must not carry its own list."*
 *
 * ERR-DSH-10-03 is the prototype defect behind it: all three report screens
 * carried a standalone three-row list — `Main Warehouse`, `Branch 2 —
 * Oakland`, `Transit Hub` — of which the last two exist in no master record,     report-warehouse-list:ignore -- naming the prototype's rows IS the warning
 * no address, no stock, nothing, while three real warehouses appeared in no
 * report filter at all. The build did not reproduce that (it rendered no
 * picker whatsoever), so this is the rule being met for the first time rather
 * than a defect being repaired.
 *
 * ── WHAT "WAREHOUSES THAT HOLD STOCK" MEANS, AND WHY ───────────────────────
 *
 * The rule answers itself in its own second clause: **"the same set the stock
 * screens offer."** `INV-04 §5.4` names that set — the master's six records
 * minus the virtual one and the inactive one, four — and calls the exclusion
 * *"correct behaviour"*. The predicate already exists in this build, for
 * INV-05's Warehouses card:
 *
 *     SELECT count(*) FROM warehouses WHERE is_active AND type <> 'virtual'
 *     — `stock.service.ts`: "Card 5 counts STOCK-BEARING locations"
 *
 * `stockBearing` below is that same predicate, in the browser, so the two
 * cannot drift into disagreeing about what the product means by the phrase.
 *
 * **It is a property of the MASTER RECORD — a kind of location, switched on —
 * and NOT a quantity on hand, and NOT movement inside the report's period.**
 * Both of those readings are available and both are wrong here:
 *
 *  - *Currently holds a quantity* would drop a live branch that happens to be
 *    empty today, on the report whose purpose is to ask why.
 *  - *Held stock during the period* would empty the filter exactly on a quiet
 *    window — the reader looking for the site that went silent would be
 *    offered every site except that one.
 *
 * Either would also make the filter's contents depend on the figures it is
 * meant to narrow, so opening the picker would answer a question the reader
 * had not asked yet.
 *
 * ── WHY `<Typeahead>` AND NOT A `<select>` ─────────────────────────────────
 *
 * GEN-50: *"Every 'Pick from list' field must offer a search box inside its
 * dropdown once the list holds more than 10 records."* Every other warehouse
 * control in the build is a plain `<select>` populated from `useWarehouses`,
 * which is correct for the tenants that have four locations and silently
 * wrong for the ones that have thirty — and a report filter is precisely the
 * control a large tenant uses most. `<Typeahead>` never fetches: it takes the
 * options it is given, so it serves a small list held in memory exactly as
 * well as a searched one, and this file needed no new endpoint to use it.
 *
 * The one deliberate difference from the other pickers is that the narrowing
 * happens HERE rather than on the server. That is safe only because the whole
 * candidate list is already in the browser — the hazard the server predicates
 * exist to prevent (a page-sized result silently hiding matches) cannot arise
 * over a list that is not paged.
 *
 * ── ONE MORE THING THE `id` IS DOING ───────────────────────────────────────
 *
 * BR-DSH-10-19: *"A report must never join a warehouse by an internal key that
 * is reused for a different warehouse elsewhere."* The prototype's list reused
 * `wh1` for a DIFFERENT warehouse than the master's `wh1`, so a join on it      report-warehouse-list:ignore -- naming the colliding key IS the warning
 * attributed one site's figures to another and every total still added up.
 * Nothing here can mint such a key: the value handed to the query is the
 * master record's own identifier, taken from the record the reader picked.
 */

/**
 * `is_active AND type <> 'virtual'` — the stock-bearing predicate, mirrored
 * from `stock.service.ts`.
 *
 * Named and exported rather than inlined into the `useMemo` below because it
 * is the RULE, not a detail of this control: the next screen that needs the
 * same set takes it from here instead of writing the condition a third time,
 * which is how the product came to hold three different warehouse lists in
 * the first place (`INV-04 §5.4`). It is exercised through the rendered
 * picker in `reports-warehouse-filter.test.tsx`, not by a direct import — the
 * assertions are about what a reader is offered.
 */
export function stockBearing(warehouses: readonly WarehouseResponse[] | undefined): WarehouseResponse[] {
  return (warehouses ?? []).filter((warehouse) => warehouse.isActive && warehouse.type !== 'virtual');
}

/** The row a reader scans: the name, with the CODE as the quiet second half. */
function warehouseOption(warehouse: WarehouseResponse): TypeaheadOption {
  return { id: warehouse.id, label: warehouse.name, hint: warehouse.code };
}

/**
 * Name or code, case-insensitively. The code is searchable because it is what
 * a warehouse is called on paper — somebody reading a delivery note has
 * `WH-002` in front of them and not "Downtown Retail Store".
 */
function matches(warehouse: WarehouseResponse, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle === '') return true;
  return warehouse.name.toLowerCase().includes(needle) || warehouse.code.toLowerCase().includes(needle);
}

export function WarehouseFilter({
  value,
  onChange,
}: {
  /** The chosen warehouse's id, or null for "All warehouses". */
  value: string | null;
  onChange: (warehouseId: string | null) => void;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = React.useState('');

  /*
   * `status: 'active'` is the half of "stock-bearing" the SERVER owns, and it
   * is asked for rather than filtered out afterwards for BR-DSH-10-13's
   * reason: a record that reached the browser is already out. The virtual
   * half has no server-side query parameter, so it is applied here — and
   * `isActive` is re-applied beside it, because `stockBearing` is a statement
   * about the rule and not about which endpoint happened to answer.
   */
  const { data: warehouses } = useWarehouses({ status: 'active' });

  const offered = React.useMemo(() => stockBearing(warehouses), [warehouses]);
  const options = React.useMemo(
    () => offered.filter((warehouse) => matches(warehouse, query)).map(warehouseOption),
    [offered, query],
  );
  const selected = React.useMemo(() => {
    const held = offered.find((warehouse) => warehouse.id === value);
    return held === undefined ? null : warehouseOption(held);
  }, [offered, value]);

  const label = t('reports.filterWarehouse');

  return (
    <div className="flex flex-col gap-1">
      {/* Band 2's shape: "a grid of dropdowns, each with a small label above it". */}
      <label className="text-xs text-muted-foreground" htmlFor="report-warehouse">
        {label}
      </label>
      <Typeahead
        id="report-warehouse"
        label={label}
        value={value}
        onChange={onChange}
        options={options}
        selected={selected}
        query={query}
        onQueryChange={setQuery}
        size="cell"
        /*
         * BR-DSH-10-09: *"Every filter dropdown must offer All, and All must be
         * its default."* Band 2 says the same thing in prose — "Every dropdown
         * offers All as its first choice, and All is the delivered default for
         * all of them."
         *
         * Both halves live in two places and this is the first: `clearLabel`
         * makes All a ROW in the panel rather than a button beside it, so one
         * arrow-key walk covers every choice the control offers — including
         * taking the narrowing off. The second half is each page's
         * `useState<string | null>(null)`, which is what "delivered default"
         * means: the screen is already on All when it loads, rather than
         * offering All as something a reader can get back to.
         *
         * ── THE CAVEAT, STATED BECAUSE THE RULE SAYS "EVERY" ─────────────────
         *
         * This holds for every filter dropdown the build DRAWS, and that is
         * exactly ONE per screen. DSH-05 §5a specifies four (Salesperson,
         * Customer, Category, Warehouse), DSH-06 four and DSH-07 its own set;
         * only Warehouse / Branch exists, on all three, and the pages carry a
         * hint saying so. So "every dropdown offers All" is true of the screens
         * as built and is not yet a statement about the four-dropdown band the
         * spec describes. Each picker that arrives has to bring this pair with
         * it, and there is no shared mechanism forcing it to — which is why the
         * requirement is written down here rather than assumed.
         */
        clearLabel={t('reports.allWarehouses')}
        /*
         * THE SENTENCE A SEARCH MATCHING NOTHING SHOWS. `<Typeahead>` draws it
         * when `options.length === 0` — below the rows, with
         * `role="presentation"` — so it appears BESIDE the All row rather than
         * instead of it, which is right: All is an action the control always
         * offers, and it cannot answer the question "what did my search find?"
         *
         * IT WAS UNREACHABLE WHEN THIS FILE WAS WRITTEN, and the reason is
         * worth keeping rather than deleting as stale. The notice used to key
         * off `rows.length === 0`, and `rows` is `[null, ...options]` whenever
         * a `clearLabel` is given — so for every picker with a "no record" row
         * the panel could never BE empty and the sentence could never be said;
         * a search matching nothing drew All ALONE, reading as "here is your
         * one result". This call site passed `emptyLabel` anyway and said so;
         * a later wave fixed the component, and the sentence now renders.
         *
         * The lesson outlives the bug: **a row the control always offers is
         * the wrong thing to count when asking what a search returned.**
         */
        emptyLabel={t('picker.noMatchingWarehouse')}
      />
    </div>
  );
}
